import { downloadScanPalletLabel, getOrder } from "@/apis/tms/order";
import { useQuery } from "@tanstack/react-query";

import { query2BOrders, query2COrders } from "@/apis/cube/order";
import {
  Cube2COrder,
  Cube2BOrder,
  TMSPalletVo,
} from "@/types";
import { ColDef, ICellRendererParams } from "ag-grid-community";

export function useScanOrderPalletLabel(orderId: string, palletId: string) {
  return useQuery({
    queryKey: [`order-${orderId}-pallet-${palletId}-label`],
    queryFn: () => downloadScanPalletLabel(orderId, palletId),
    enabled: orderId != null && palletId != null,
  });
}

export async function getTMSOrder(orderIdOrRIIOrderNo: string) {
  if (orderIdOrRIIOrderNo == null) return Promise.resolve(null);
  return await getOrder(orderIdOrRIIOrderNo);
}

export function useTMSOrder(orderIdOrRIIOrderNo: string) {
  return useQuery({
    queryKey: ["tms-order", orderIdOrRIIOrderNo],
    queryFn: () => getTMSOrder(orderIdOrRIIOrderNo),
    placeholderData: (previousData) => previousData,
    enabled: !!orderIdOrRIIOrderNo,
    refetchOnWindowFocus: false,
  });
}

export async function getCubeOrderDetail(
  cubeOrderNo: string | undefined,
): Promise<Cube2COrder | Cube2BOrder | null> {
  if (!cubeOrderNo) return Promise.resolve(null);
  if (cubeOrderNo.startsWith("OTIN") || cubeOrderNo.startsWith("CION")) {
    return query2COrders({ instructionNumber: cubeOrderNo }).then((res) => {
      if (res.items.length > 0) {
        return res.items[0];
      }
      return null;
    });
  } else if (cubeOrderNo.startsWith("SUB")) {
    return query2BOrders({ deliveryNos: [cubeOrderNo] }).then((res) => {
      if (res.items.length > 0) {
        return res.items[0];
      }
      return null;
    });
  }
  return Promise.resolve(null);
}

export function useCubeOrderDetail(cubeOrderNo: string | undefined) {
  return useQuery({
    queryKey: ["cube-order-detail", cubeOrderNo],
    queryFn: () => getCubeOrderDetail(cubeOrderNo),
    retry: false,
    enabled: !!cubeOrderNo,
    refetchOnWindowFocus: false,
  });
}

export const PalletDimensionCol: ColDef<TMSPalletVo> = {
  headerName: "Dimension",
  field: "dimension",
  resizable: false,
  minWidth: 120,
  maxWidth: 120,
  suppressMenu: true,
  sortable: false,
  rowDrag: false,
  cellRenderer: ({ data }: ICellRendererParams<TMSPalletVo, string>) => {
    return (
      <div className="flex flex-col text-xs">
        <div>W: {data?.dimension?.width}m</div>
        <div>H: {data?.dimension?.height}m</div>
        <div>L: {data?.dimension?.length}m</div>
        <div>Wt: {data?.dimension?.weight}kg</div>
      </div>
    );
  },
};
